import { useCallback, useEffect, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { fetchChannelsIndex } from '../../api/channels';
import { getApiErrorMessage } from '../../api/http';
import {
  epgAutoMapAll,
  epgAutoMapSelected,
  fetchEpgSources,
  type EpgSourceRow,
} from '../../api/epgApi';
import { useToast } from '../../context/ToastContext';
import { useEditorSession } from '../../context/EditorSessionContext';
import { usePlaylist } from '../../context/PlaylistContext';
import { applyEpgShiftFromNamesForChannels } from '../../lib/applyEpgShiftFromNames';
import {
  EPG_EDITOR_RELOAD_EVENT,
  EPG_EDITOR_STATE_EVENT,
  type EpgEditorStateDetail,
} from '../../dashboard/epgEditorEvents';

type Scope = 'selected' | 'without-epg' | 'all';

function epgIdEmpty(v: string | number | null | undefined): boolean {
  if (v == null) return true;
  return String(v).trim() === '';
}

export default function EpgAssignmentAssistantModal({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const { playlistId } = usePlaylist();
  const { getSharedEditorState } = useEditorSession();

  const [sources, setSources] = useState<EpgSourceRow[]>([]);
  const [sourceId, setSourceId] = useState('');
  const [scope, setScope] = useState<Scope>('selected');
  const [applyShift, setApplyShift] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editorActiveSourceId, setEditorActiveSourceId] = useState<number | null>(null);

  useEffect(() => {
    const onState = (ev: Event) => {
      const detail = (ev as CustomEvent<EpgEditorStateDetail>).detail;
      if (detail && 'activeSourceId' in detail) {
        setEditorActiveSourceId(detail.activeSourceId ?? null);
      }
    };
    window.addEventListener(EPG_EDITOR_STATE_EVENT, onState);
    return () => window.removeEventListener(EPG_EDITOR_STATE_EVENT, onState);
  }, []);

  useEffect(() => {
    if (!show) {
      setBusy(false);
      setScope('selected');
      setApplyShift(true);
      return;
    }
    void (async () => {
      try {
        const list = await fetchEpgSources();
        setSources(list);
        const pre =
          editorActiveSourceId != null && list.some((s) => s.id === editorActiveSourceId)
            ? String(editorActiveSourceId)
            : list[0]?.id != null
              ? String(list[0].id)
              : '';
        setSourceId(pre);
      } catch {
        setSources([]);
        setSourceId('');
      }
    })();
  }, [show, editorActiveSourceId]);

  const run = useCallback(async () => {
    if (!playlistId) {
      showToast('First select a playlist from Playlist manager.', 'warning');
      return;
    }
    const sid = Number(sourceId);
    if (!Number.isFinite(sid) || sid <= 0) {
      showToast('Select an EPG source', 'warning');
      return;
    }

    setBusy(true);
    try {
      if (scope === 'all') {
        showToast('Auto-mapping EPG for all streams…', 'info');
        const r = await epgAutoMapAll(playlistId, sid);
        if (!r.success) {
          showToast(r.message || 'Auto-mapping failed', 'error');
          return;
        }
        showToast(`Assigned EPG ID for ${r.count ?? 0} channels`, 'success');
        if (applyShift) {
          const fresh = await fetchChannelsIndex(playlistId);
          const n = await applyEpgShiftFromNamesForChannels(playlistId, fresh);
          if (n > 0) showToast(`Applied shift from names on ${n} channels`, 'success');
        }
        window.dispatchEvent(new Event(EPG_EDITOR_RELOAD_EVENT));
        onClose();
        return;
      }

      let channelIds: number[] = [];
      if (scope === 'selected') {
        channelIds = getSharedEditorState(playlistId).selectedIds.filter((id) => id > 0);
        if (channelIds.length === 0) {
          showToast('No channels selected in the EPG editor', 'warning');
          return;
        }
      } else {
        const all = await fetchChannelsIndex(playlistId);
        channelIds = all.filter((ch) => epgIdEmpty(ch.epg_id)).map((ch) => ch.id);
        if (channelIds.length === 0) {
          showToast('All channels already have an EPG ID', 'info');
          return;
        }
      }

      showToast(`Searching EPG for ${channelIds.length} channels…`, 'info');
      const res = await epgAutoMapSelected(playlistId, channelIds, sid);
      if (!res.success) {
        showToast(res.message || 'Auto-mapping failed', 'error');
        return;
      }

      const updated = res.updated ?? 0;
      const total = res.total ?? channelIds.length;
      showToast(`Mapped EPG for ${updated} of ${total} channels`, 'success');
      if (updated < total) {
        showToast(`No EPG match for ${total - updated} channels`, 'info');
      }

      if (applyShift) {
        const fresh = await fetchChannelsIndex(playlistId);
        const mappedIds = new Set(
          (Array.isArray(res.results) ? res.results : [])
            .filter((row) => row.status === 'success' && typeof row.channel_id === 'number')
            .map((row) => row.channel_id as number),
        );
        const targets =
          mappedIds.size > 0 ? fresh.filter((ch) => mappedIds.has(ch.id)) : fresh;
        const n = await applyEpgShiftFromNamesForChannels(playlistId, targets);
        if (n > 0) showToast(`Applied shift from names on ${n} channels`, 'success');
      }

      window.dispatchEvent(new Event(EPG_EDITOR_RELOAD_EVENT));
      onClose();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'EPG assignment failed'), 'error');
    } finally {
      setBusy(false);
    }
  }, [
    applyShift,
    getSharedEditorState,
    onClose,
    playlistId,
    scope,
    showToast,
    sourceId,
  ]);

  return (
    <SimpleModal
      show={show}
      title="EPG assignment assistant"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void run()} disabled={busy}>
            {busy ? 'Running…' : 'Run'}
          </button>
        </>
      }
    >
      <p className="text-muted small mb-3">
        Automatically match stream names to EPG channel IDs from a source. Requires an EPG source
        with loaded programme data.
      </p>

      <div className="mb-3">
        <label className="form-label">EPG source</label>
        <select
          className="form-select"
          value={sourceId}
          onChange={(e) => setSourceId(e.target.value)}
          disabled={busy || sources.length === 0}
        >
          {sources.length === 0 ? (
            <option value="">No sources available</option>
          ) : (
            sources.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.name}
              </option>
            ))
          )}
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label">Scope</label>
        <div className="d-flex flex-column gap-2">
          <label className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="epg-assistant-scope"
              checked={scope === 'selected'}
              onChange={() => setScope('selected')}
              disabled={busy}
            />
            <span className="form-check-label">Selected channels</span>
          </label>
          <label className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="epg-assistant-scope"
              checked={scope === 'without-epg'}
              onChange={() => setScope('without-epg')}
              disabled={busy}
            />
            <span className="form-check-label">Channels without EPG</span>
          </label>
          <label className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="epg-assistant-scope"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
              disabled={busy}
            />
            <span className="form-check-label">All channels</span>
          </label>
        </div>
      </div>

      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="epg-assistant-shift"
          checked={applyShift}
          onChange={(e) => setApplyShift(e.target.checked)}
          disabled={busy}
        />
        <label className="form-check-label" htmlFor="epg-assistant-shift">
          Apply shift from names (when shift is 0)
        </label>
      </div>
    </SimpleModal>
  );
}
